/**
 * Enum for wrap node types in the sequencer.
 *
 * Wrap nodes are paired Start/End markers that contain other sequencer nodes.
 * Each wrap type has specific deletion methods and behaviors.
 *
 * @enum {string}
 */
const WrapNodeType = {
    /** Music background wrap - adds background music to selected content */
    MUSIC: 'MUSIC_WRAP',

    /** Floating overlay wrap - B-roll video overlay with grid positioning */
    FLOATING_OVERLAY: 'FLOATING_OVERLAY_WRAP',

    /** Fullscreen overlay wrap - Full-screen B-roll video overlay */
    FULLSCREEN_OVERLAY: 'FULLSCREEN_OVERLAY_WRAP',

    /** Layout wrap - Multi-panel layout configuration */
    LAYOUT: 'LAYOUT_WRAP',

    /** Zoom effect wrap - Ken Burns style zoom animation */
    ZOOM_EFFECT: 'ZOOM_EFFECT_WRAP',

    /** Dynamic caption wrap - Animated word-by-word captions */
    DYNAMIC_CAPTION: 'DYNAMIC_CAPTION_WRAP',

    /** Title wrap - Static text overlay using project theme */
    TITLE: 'TITLE_WRAP',

    /** Component wrap - Motion Studio component overlay (lower third, title slide, etc.) */
    COMPONENT: 'COMPONENT_WRAP',

    /** Mute wrap - silences contained audio range without removing words */
    MUTE: 'MUTE_WRAP'
};

const WRAP_NODE_TYPE_VALUES = Object.freeze([
    WrapNodeType.MUSIC,
    WrapNodeType.FLOATING_OVERLAY,
    WrapNodeType.FULLSCREEN_OVERLAY,
    WrapNodeType.LAYOUT,
    WrapNodeType.ZOOM_EFFECT,
    WrapNodeType.DYNAMIC_CAPTION,
    WrapNodeType.TITLE,
    WrapNodeType.COMPONENT,
    WrapNodeType.MUTE,
]);
const LEGACY_GENERIC_WRAP_CLASS_NAMES = new Set([
    'StartWrapSequencerNode',
    'EndWrapSequencerNode',
]);

/**
 * Maps wrap node types to their corresponding deletion methods on the Sequencer model.
 * @type {Object.<WrapNodeType, string>}
 */
WrapNodeType.DELETION_METHOD_MAP = {
    [WrapNodeType.MUSIC]: 'deleteMusicWrapNodeCreated',
    [WrapNodeType.FLOATING_OVERLAY]: 'deleteBRollWrapNodeCreated',
    [WrapNodeType.FULLSCREEN_OVERLAY]: 'deleteFullscreenOverlayWrapNodeCreated',
    [WrapNodeType.LAYOUT]: 'deleteLayoutWrapNodes',
    [WrapNodeType.ZOOM_EFFECT]: 'deleteZoomEffectWrapNodeCreated',
    [WrapNodeType.DYNAMIC_CAPTION]: 'deleteDynamicCaptionWrapNodes',
    [WrapNodeType.TITLE]: 'deleteTitleWrapNodes',
    [WrapNodeType.COMPONENT]: 'deleteComponentWrapNodes',
    [WrapNodeType.MUTE]: 'deleteMuteWrapNodeCreated'
};

/**
 * Gets the deletion method name for a given wrap node type.
 * @param {WrapNodeType} wrapType - The wrap node type
 * @returns {string|null} The method name or null if not found
 */
WrapNodeType.getDeletionMethod = function(wrapType) {
    return WrapNodeType.DELETION_METHOD_MAP[wrapType] || null;
};

/**
 * Set of visual wrap node types that affect video output but don't produce audio.
 * Used for centralized detection of visual wrap nodes throughout the codebase.
 *
 * NOTE: MUSIC is explicitly EXCLUDED - it produces audio that merges with content.
 *
 * @type {Set<WrapNodeType>}
 */
WrapNodeType.VISUAL_WRAP_TYPES = new Set([
    WrapNodeType.FLOATING_OVERLAY,   // B-roll video overlay
    WrapNodeType.FULLSCREEN_OVERLAY, // Full-screen B-roll
    WrapNodeType.ZOOM_EFFECT,        // Ken Burns zoom animation
    WrapNodeType.LAYOUT,             // Multi-panel layout
    WrapNodeType.DYNAMIC_CAPTION,    // Animated captions
    WrapNodeType.TITLE,              // Static text overlay
    WrapNodeType.COMPONENT,          // Motion Studio component overlay
]);

/**
 * Checks if a wrap node type is a visual wrap type (affects video, no audio).
 *
 * Visual wrap nodes:
 * - Are scheduled for video rendering
 * - Have visual gap duration (no audio gaps)
 * - Should be skipped in audio buffer loading
 * - Should NOT be merged with word nodes in scheduling
 *
 * @param {WrapNodeType|string} wrapType - The wrap type to check
 * @returns {boolean} True if this is a visual wrap type
 */
WrapNodeType.isVisualWrapType = function(wrapType) {
    return WrapNodeType.VISUAL_WRAP_TYPES.has(wrapType);
};

/**
 * Checks if a given string is a valid wrap node type.
 * @param {string} type - The type to check
 * @returns {boolean} True if valid wrap node type
 */
WrapNodeType.isValid = function(type) {
    return WRAP_NODE_TYPE_VALUES.includes(type);
};

function getSequencerNodeClassName(sequencerNode) {
    if (!sequencerNode) {
        return null;
    }

    const nodeConstructor = typeof sequencerNode.getConstructor === 'function'
        ? sequencerNode.getConstructor()
        : sequencerNode.constructor;

    return nodeConstructor && nodeConstructor.CLASSNAME
        ? nodeConstructor.CLASSNAME
        : null;
}

/**
 * Legacy saved projects can still deserialize the abstract generic wrap pair.
 * Those nodes are supported separately from truly unknown wrap classes.
 *
 * @param {Object|null} sequencerNode
 * @returns {boolean}
 */
WrapNodeType.isLegacyGenericWrapSequencerNode = function(sequencerNode) {
    return LEGACY_GENERIC_WRAP_CLASS_NAMES.has(getSequencerNodeClassName(sequencerNode));
};

/**
 * Resolves a sequencer wrap node instance to its wrap type.
 *
 * This avoids depending on ad-hoc runtime helpers on Utils and instead keeps
 * wrap-node classification next to the wrap-type registry itself.
 *
 * @param {Object|null} sequencerNode
 * @returns {WrapNodeType|null}
 */
WrapNodeType.getWrapNodeTypeForSequencerNode = function(sequencerNode) {
    const className = getSequencerNodeClassName(sequencerNode);

    switch (className) {
        case 'StartMusicWrapSequencerNode':
        case 'EndMusicWrapSequencerNode':
            return WrapNodeType.MUSIC;
        case 'StartFloatingOverlayWrapSequencerNode':
        case 'EndFloatingOverlayWrapSequencerNode':
            return WrapNodeType.FLOATING_OVERLAY;
        case 'StartFullscreenOverlayWrapSequencerNode':
        case 'EndFullscreenOverlayWrapSequencerNode':
            return WrapNodeType.FULLSCREEN_OVERLAY;
        case 'StartLayoutWrapSequencerNode':
        case 'EndLayoutWrapSequencerNode':
            return WrapNodeType.LAYOUT;
        case 'StartZoomEffectWrapSequencerNode':
        case 'EndZoomEffectWrapSequencerNode':
            return WrapNodeType.ZOOM_EFFECT;
        case 'StartDynamicCaptionWrapSequencerNode':
        case 'EndDynamicCaptionWrapSequencerNode':
            return WrapNodeType.DYNAMIC_CAPTION;
        case 'StartTitleWrapSequencerNode':
        case 'EndTitleWrapSequencerNode':
            return WrapNodeType.TITLE;
        case 'StartComponentWrapSequencerNode':
        case 'EndComponentWrapSequencerNode':
            return WrapNodeType.COMPONENT;
        case 'StartMuteWrapSequencerNode':
        case 'EndMuteWrapSequencerNode':
            return WrapNodeType.MUTE;
        default:
            return null;
    }
};

export default WrapNodeType;
